<template>
  <div :class="[$style.container]" @dragenter.stop.prevent="onDragEnter" v-if="isReadyMatrix">
    <div v-if="!activeChatId" :class="$style.placeholder">
      <div>{{ $t('chat.selectPlaceholder') }}</div>
    </div>
    <template v-else>
      <transition name="fade">
        <div
          v-show="isDropFile"
          :class="$style.dropzone"
          @dragenter.stop.prevent="onDragEnter"
          @dragover.stop.prevent="onDragEnter"
          @dragleave.stop.prevent="onDragLeave"
          @drop.stop.prevent="onDrop"
        >
          <div :class="$style.dropzoneText">
            <BaseIcon name="upload" :class="$style.dropzoneIcon" />
            <br />
            {{ $t('chat.dropZone') }}
          </div>
        </div>
      </transition>
      <div ref="messageList" v-if="!isInviteRoom" :class="$style.messageListWrap">
        <div ref="messageListContent" :class="[$style.messageList, { [$style.messageListActive]: false }]">
          <infinite-loading
            direction="top"
            :identifier="infiniteId"
            :class="$style.infiniteLoading"
            @infinite="infiniteHandler"
          >
            <Spinner slot="spinner" />
            <div slot="no-more"></div>
            <div v-show="!messages.length && !isLoading" slot="no-results" :class="$style.placeholder">
              <div>{{ $t('chat.noMessagesPlaceholder') }}</div>
            </div>
            <div slot="error" slot-scope="{ trigger }">
              {{ $t('chat.errorMessagesLoading') }}.
              <span class="link" @click="trigger">{{ $t('chat.retryMessagesLoading') }}</span>
            </div>
          </infinite-loading>
          <Message
            v-for="(item, index) in messages"
            :key="item.age"
            ref="messages"
            v-bind="{
              message: item,
              isGrouped: isHideUser(item, index),
              isShowUserName: isHideUser(item, index),
              isShowAvatar: isHideUser(item, index, true),
              isPrevMessageEvent: isPrevMessageEvent(item, index),
              isShowDay: isShowDay(item),
              isPicked: isMessagePicked(item),
              isReadMessage: timeOfLastReadMessage > item.origin_server_ts,
              isHideIconOnHover: isHideIconOnHover(item)
            }"
            @pick="pickMessage($event)"
          />
          <button class="scroll-down" type="button" v-show="isShowScrollDownButton" @click.prevent="scrollDown">
            <svg xmlns="http://www.w3.org/2000/svg" width="32px" height="32px" viewBox="0 0 32 32" fill="none">
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M1.99951 16C1.99951 8.26801 8.26753 2 15.9995 2C23.7315 2 29.9995 8.26801 29.9995 16C29.9995 23.732 23.7315 30 15.9995 30C8.26753 30 1.99951 23.732 1.99951 16ZM11.7066 15.2929C11.3161 14.9024 10.6829 14.9024 10.2924 15.2929C9.90188 15.6834 9.90188 16.3166 10.2924 16.7071L15.2924 21.7071C15.6829 22.0976 16.3161 22.0976 16.7066 21.7071L21.7066 16.7071C22.0971 16.3166 22.0971 15.6834 21.7066 15.2929C21.3161 14.9024 20.6829 14.9024 20.2924 15.2929L16.9995 18.5858V11C16.9995 10.4477 16.5518 10 15.9995 10C15.4472 10 14.9995 10.4477 14.9995 11V18.5858L11.7066 15.2929Z"
                fill="#00b764"
              />
            </svg>
          </button>
        </div>
      </div>
      <div
        v-if="!isInviteRoom && !isLeaveRoom"
        :class="[isShowActions || (currentRoom && currentRoom.maySendMessage) ? $style.bottomWrap : '']"
      >
        <div v-if="isShowActions" :class="$style.actions">
          <div :class="$style.actionsGroup">
            <BaseButton
              v-if="isPremium && isCanTranslate"
              v-tooltip="$t('chat.actions.translate')"
              @click="onTranslate"
              oval
              :loading="isTranslating"
              :class="$style.actionsButton"
            >
              <BaseIcon name="language" />
              {{ pickedCount }}
            </BaseButton>
            <BaseButton v-tooltip="$t('chat.actions.forward')" :class="$style.actionsButton" oval @click="onForward">
              <BaseIcon name="reply-all" />
              {{ pickedCount }}
            </BaseButton>
            <BaseButton
              v-if="isShowDeleteBtn"
              v-tooltip="$t('chat.actions.delete')"
              :class="$style.actionsButton"
              oval
              @click="onDelete"
            >
              <BaseIcon name="trash-alt" />
              {{ pickedCount }}
            </BaseButton>
            <DeleteMatrixMessages :message="pickedMessages" :beforeDelete="clearPickedMessages" />
            <template v-if="isShowSingleActions">
              <BaseButton
                v-show="currentRoom && currentRoom.maySendMessage"
                v-tooltip="$t('chat.actions.reply')"
                :class="$style.actionsButton"
                oval
                @click="onReply"
              >
                <BaseIcon name="share" />
                {{ pickedCount }}
              </BaseButton>
              <BaseButton
                v-if="isShowEdit && !isWAChat"
                oval
                :class="$style.actionsButton"
                v-tooltip="$t('chat.actions.edit')"
                @click="onEdit"
              >
                <BaseIcon name="pencil-alt" />
                {{ pickedCount }}
              </BaseButton>
            </template>
          </div>
          <BaseButton @click="clearPickedMessages" oval secondary :class="$style.actionsButton"
            >{{ $t('chat.actions.cancel') }}
          </BaseButton>
        </div>
        <template v-if="currentRoom && currentRoom.maySendMessage && !isShowActions">
          <VoiceRecorder
            v-if="isVoiceRecording"
            :output-format="voiceRecorderFormat"
            @close="isVoiceRecording = false"
            @recorded="sendVoice"
          />
          <template v-else>
            <div :class="$style.extraBlock">
              <MessageReplyMatrix v-if="repliedMessage" :message="repliedMessage" :room="currentRoomFully">
                <BaseIcon @click="clearReply" custom name="close" :class="$style.replyClose" />
              </MessageReplyMatrix>
              <MessageReplyMatrix v-if="editedMessage" :room="currentRoomFully" :message="editedMessage" isEdit>
                <BaseIcon @click="clearEdit" custom name="close" :class="$style.replyClose" />
              </MessageReplyMatrix>
              <div v-if="attachment" :class="$style.attachmentPreview">
                <BaseIcon name="paperclip" />
                <div :class="$style.attachmentPreviewBody">
                  <span :class="$style.attachmentPreviewName">{{ attachment.name }}</span>
                  <span :class="$style.attachmentPreviewSize">({{ attachmentSize }})</span>
                </div>
                <BaseIcon @click="clearAttachment" custom name="close" :class="$style.attachmentPreviewClose" />
              </div>
            </div>
            <div :class="[$style.sendMessageForm]">
              <div :class="$style.avatarContainer">
                <Avatar :user="user" :size="40" />
              </div>
              <div :class="$style.messageContainer">
                <textarea
                  v-model="text"
                  :placeholder="$t('chat.inputPlaceholder')"
                  ref="textarea"
                  rows="3"
                  :class="$style.input"
                  @keydown.enter.exact.prevent
                  @keyup.enter.exact="onSubmit"
                  @keyup.shift.enter.exact="() => {}"
                  @keyup.up.exact="onTextKeyupUp"
                  @input="onTextInput"
                ></textarea>
              </div>
              <div :class="[$style.buttons]">
                <div :class="$style.buttonsMore">
                  <v-popover
                    placement="top-end"
                    offset="5"
                    :popoverInnerClass="['tooltip-inner popover-inner', $style.picker]"
                  >
                    <BaseIcon :name="['far', 'smile']" :class="[$style.btnMore, 'tooltip-target']" />
                    <Picker slot="popover" provider="tenor" @insert:emoji="insertToText" @insert:gif="sendGif" />
                  </v-popover>
                  <FileInput ref="fileInput1" @input="onFileChange" accept="image/png, image/jpeg">
                    <BaseIcon :name="'camera'" :class="$style.btnMore" />
                  </FileInput>
                  <FileInput ref="fileInput2" @input="onFileChange">
                    <BaseIcon :name="'paperclip'" :class="$style.btnMore" />
                  </FileInput>
                  <BaseIcon @click="onVoice" name="microphone" :class="$style.btnMore" />
                </div>
                <button @click="onSubmit" :class="$style.buttonSend">
                  {{ $t('chat.actions.send') }}
                  <BaseIcon custom name="locationArrow" :class="$style.buttonSendIcon" />
                </button>
              </div>
            </div>
          </template>
        </template>
      </div>
      <div v-if="isInviteRoom" :class="$style.inviteContainer">
        <div :class="$style.inviteMessageIcon">
          <svg xmlns="http://www.w3.org/2000/svg">
            <path
              d="M22.5 22.55h3V10h-3Zm1.5 7.1q.7 0 1.175-.475.475-.475.475-1.175 0-.7-.475-1.175Q24.7 26.35 24 26.35q-.7 0-1.175.475-.475.475-.475 1.175 0 .7.475 1.175.475.475 1.175.475ZM4 44V7q0-1.15.9-2.075Q5.8 4 7 4h34q1.15 0 2.075.925Q44 5.85 44 7v26q0 1.15-.925 2.075Q42.15 36 41 36H12Zm3-7.25L10.75 33H41V7H7ZM7 7v29.75Z"
            />
          </svg>
        </div>
        <p :class="$style.inviteTitle">
          {{ currentRoom.typeName === 'group' ? $t('doYouWannaChatWith') : $t('doYouWannaJoin') }}
          <strong>
            {{ formattedUserName(currentRoom.name) }}
          </strong>
        </p>
        <div :class="$style.inviteDescription">
          {{ $t(showMoreText ? 'inviteMatrixChatFully' : 'inviteMatrixChatShort') }}
          <a href="#" @click.prevent="showMoreText = !showMoreText">
            {{ $t(showMoreText ? 'showLess' : 'learnMore') }}
          </a>
        </div>
        <div :class="$style.inviteBtns">
          <BaseButton red :loading="isLoadingAcceptInvite" @click="acceptInvite">{{ $t('acceptInvite') }}</BaseButton>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import Avatar from '@/components/Avatar'
import DeleteMatrixMessages from '@/components/modals/DeleteMatrixMessages.vue'
import FileInput from '@/components/FileInput'
import InfiniteLoading from 'vue-infinite-loading'
import Message from './MessageMatrix'
import MessageReplyMatrix from './MessageReplyMatrix'
import Picker from './Picker'
import Spinner from '@/components/Spinner'
import VoiceRecorder from './VoiceRecorder'
import {
  createLinkImage,
  formattedRoomItem,
  formattedUserName,
  parseRouterQueryParam,
  createListenerForClickOnTagA
} from '@/helpers'

import { mapActions, mapGetters, mapMutations } from 'vuex'
import { startOfDay } from 'date-fns'
import moment from 'moment'
import { TYPE_CODE } from '@/models/Message'
import chunkText from 'chunk-text'
import { cloneDeep, debounce, findLast, get, throttle } from 'lodash'
import urlRegex from 'url-regex'
import { eventTypes, msgTypes, selfMembershipTypes, messengerProviders } from '@/helpers/variables'
import superbytes from 'superbytes'
import api from '@/api'

const FILE_SIZE_LIMIT = 1000
const FILE_LIMIT_WA = {
  image: 16,
  video: 16,
  audio: 16,
  other: 100
}

export default {
  name: 'chat-view-matrix',
  components: {
    Avatar,
    DeleteMatrixMessages,
    FileInput,
    InfiniteLoading,
    Message,
    MessageReplyMatrix,
    Picker,
    Spinner,
    VoiceRecorder
  },
  props: {
    highlightMessageId: Number
  },
  data () {
    return {
      text: '',
      pickedMessages: [],
      repliedMessage: null,
      editedMessage: null,
      infiniteId: +new Date(),
      attachment: null,
      isDropFile: false,
      isTranslating: false,
      isVoiceRecording: false,
      isTyping: false,
      isLoadingAcceptInvite: false,

      nextPaginationToken: null,
      matrixUser: null,
      isShowScrollDownButton: false,
      showMoreText: false
    }
  },
  watch: {
    activeChatId: {
      async handler () {
        if (this.isReadyMatrix) {
          await this.onChatChange()
        }
      },
      immediate: true
    },
    async 'messages.length' (newLength, oldLength) {
      await this.$nextTick()
      if (!this.$refs.messageList) return
      const { scrollTop, scrollHeight, offsetHeight } = this.$refs.messageList
      if ((oldLength === 0 && newLength > 0) || scrollTop > scrollHeight - offsetHeight * 2) {
        this.goBottom()
      }
    },
    isReadyMatrix () {
      if (this.isReadyMatrix) {
        setTimeout(() => {
          const { textarea, messageList, messageListContent } = this.$refs
          this.resizeObserver = new ResizeObserver(() => {
            const { scrollTop, scrollHeight, offsetHeight } = messageList
            if (scrollTop > scrollHeight - offsetHeight * 2) {
              this.goBottom()
            }
          })
          messageListContent && this.resizeObserver.observe(messageListContent)

          this.clickOnTagAToOpenANewTab = createListenerForClickOnTagA(messageListContent)
          messageListContent.addEventListener('click', this.clickOnTagAToOpenANewTab, true)

          this.setRoomMembers([])
          if (!this.isInviteRoom) {
            messageList.addEventListener('scroll', debounce(this.handleScroll, 400))
          }
          document.onpaste = event => {
            const file = this.getFileFromPasteEvent(event)
            if (!file) {
              return
            }

            if (textarea === document.activeElement) {
              this.onFileChange(file)
            }
            setTimeout(() => {
              this.text = ''
            }, 1)
          }
          window.matrixSDK.on('event', this.matrixEventHandler)
          this.onChatChange()
        }, 100)
      }
    }
  },
  computed: {
    ...mapGetters({
      isLoading: 'matrix/isLoading',
      messages: 'matrix/currentRoomMessages',
      roomMembers: 'matrix/roomMembers',
      currentRoom: 'matrix/currentRoom',
      isReadyMatrix: 'matrix/isReadyMatrix',
      user: 'auth/user',
      isPremium: 'auth/isPremium',
      chatById: 'entities/chats/getById',
      messagesByChat: 'entities/messages/listByChatId',
      messageById: 'entities/messages/find'
    }),
    activeChatId () {
      return parseRouterQueryParam(this.$route.query.id, true)
    },
    firstMessagesByDay () {
      const result = this.messages.reduce((res, message) => {
        if (message.deleted) return res
        const dayBeginTimestamp = moment(message.origin_server_ts).startOf('day').format('x')
        const savedMessage = res[dayBeginTimestamp]
        if (!savedMessage || message.origin_server_ts < savedMessage.origin_server_ts) {
          res[dayBeginTimestamp] = message
        }
        return res
      }, {})
      return result
    },
    currentRoomFully () {
      return window.matrixSDK.getRoom(this.activeChatId)
    },
    isShowDeleteBtn () {
      const [{ sender }] = this.pickedMessages
      const room = this.currentRoomFully
      const roomCreator = room.currentState.members[room.myUserId]
      const myPowerLevel = roomCreator.powerLevel
      return myPowerLevel >= 50 || sender === room.myUserId
    },
    isShowActions () {
      return !!this.pickedCount
    },
    isShowSingleActions () {
      return !!this.pickedMessages.length
    },
    isShowEdit () {
      const [{ sender, type, content }] = this.pickedMessages
      return (
        sender === this.currentRoomFully.myUserId &&
        type === eventTypes.message &&
        content.msgtype !== msgTypes.image &&
        content.msgtype !== msgTypes.video &&
        content.msgtype !== msgTypes.file &&
        content.msgtype !== msgTypes.audio
      )
    },
    pickedCount () {
      return this.pickedMessages.length
    },
    isInviteRoom () {
      return this.currentRoom && this.currentRoom.selfMembership === selfMembershipTypes.invite
    },
    isLeaveRoom () {
      return this.currentRoom && this.currentRoom.selfMembership === selfMembershipTypes.leave
    },
    voiceRecorderFormat () {
      return this.currentRoom &&
        this.currentRoom.subProvider &&
        this.currentRoom.subProvider === messengerProviders.whatsapp
        ? 'aac'
        : 'mp3'
    },
    myUserId () {
      return window.matrixSDK.credentials.userId
    },
    isCanTranslate () {
      return this.pickedMessages && this.pickedMessages[0].content.msgtype === msgTypes.text
    },
    isWAChat () {
      return this.currentRoom.subProvider === messengerProviders.whatsapp
    },
    timeOfLastReadMessage () {
      return Object.values(this.currentRoom?.receiptCacheByEventId ?? {})
        .flat()
        .filter(({ type, userId }) => type === msgTypes.read && userId !== this.myUserId)
        .reduce((time, event) => Math.max(event.data.ts, time), 0)
    },
    attachmentSize () {
      return this.attachment ? superbytes(this.attachment.size) : null
    }
  },
  beforeDestroy () {
    const { messageList, messageListContent } = this.$refs
    if (this.resizeObserver) {
      messageListContent && this.resizeObserver.unobserve(messageListContent)
      this.resizeObserver.disconnect()
    }
    messageListContent.removeEventListener('click', this.clickOnTagAToOpenANewTab, true)
    messageList.removeEventListener('scroll', debounce(this.handleScroll, 400), false)
    window.matrixSDK.off('event', this.matrixEventHandler)
  },
  methods: {
    formattedUserName,
    ...mapActions({
      getMessages: 'matrix/getMessages'
    }),
    ...mapMutations({
      refreshRoom: 'matrix/REFRESH_CURRENT_ROOM',
      addMessages: 'matrix/ADD_MESSAGE_TO_CURRENT_ROOM',
      addNewMessages: 'matrix/ADD_NEW_MESSAGE_TO_CURRENT_ROOM',
      resetMessages: 'matrix/DELETE_MESSAGES_FROM_CURRENT_ROOM',
      setRoomMembers: 'matrix/SET_ROOM_MEMBERS',
      setCurrentRoom: 'matrix/SET_ROOM_BY_ID',
      removeRoom: 'matrix/REMOVE_ROOM_FROM_LIST',
      addRoom: 'matrix/ADD_ROOM_TO_LIST',
      replaceMessageProperty: 'matrix/REPLACE_MESSAGE_PROPERTY',
      removeMessage: 'matrix/REMOVE_MESSAGE',
      translateMessage: 'matrix/TRANSLATE_MESSAGE',
      replaceRoom: 'matrix/REPLACE_ROOM'
    }),
    matrixEventHandler: debounce(function () {
      const roomId = this.currentRoom?.id
      if (!roomId) return
      const room = window.matrixSDK.getRoom(roomId)
      this.refreshRoom(room)
    }, 1000),
    getFileFromPasteEvent (event) {
      const items = (event.clipboardData || event.originalEvent.clipboardData).items
      for (let index in items) {
        const item = items[index]
        if (item.kind === 'file') {
          return item.getAsFile()
        }
      }
    },
    async acceptInvite () {
      this.isLoadingAcceptInvite = true
      try {
        await window.matrixSDK.joinRoom(this.activeChatId)
        this.removeRoom({ id: this.activeChatId, type: selfMembershipTypes.invite })
        const room = window.matrixSDK.getRoom(this.activeChatId)
        room.selfMembership = selfMembershipTypes.join
        const formattedRoom = formattedRoomItem(room)
        this.addRoom(formattedRoom)
        await this.onChatChange()
        await this.$router.replace({
          name: this.$route.name,
          query: { id: window.btoa(this.activeChatId), filter: 'all' }
        })
      } finally {
        this.isLoadingAcceptInvite = false
      }
    },
    async sendAttachmentMessage ({ file = null, roomId, gifPath = null }) {
      let currentFile = file
      if (gifPath) {
        currentFile = {
          name: 'Gif',
          size: 3000,
          type: 'image/gif'
        }
      }
      try {
        const tempId = Date.now()
        const fileType = currentFile.type.split('/')[0]
        const imgSizes = {
          w: 0,
          h: 0
        }
        const msgtype =
          fileType === 'image' && !currentFile.type.includes('svg')
            ? msgTypes.image
            : fileType === 'video'
              ? msgTypes.video
              : fileType === 'audio'
                ? msgTypes.audio
                : msgTypes.file

        let url = null
        if (gifPath) {
          url = gifPath
        } else {
          url = URL.createObjectURL(currentFile)
        }
        const tempMessage = {
          id: tempId,
          type: eventTypes.message,
          room_id: roomId,
          sender: this.myUserId,
          content: {
            body: currentFile.name,
            info: {
              name: currentFile.name,
              mimetype: currentFile.type,
              size: currentFile.size
            },
            msgtype: msgtype,
            url,
            localPath: url
          },
          origin_server_ts: tempId,
          event_id: tempId,
          user_id: this.myUserId,
          age: tempId
        }

        if (msgtype === msgTypes.image) {
          if (gifPath) {
            this.addNewMessages(tempMessage)
            this.goBottomDelayed()
          } else {
            const img = new Image()
            img.src = url
            img.onload = $event => {
              imgSizes.w = $event.path[0].width
              imgSizes.h = $event.path[0].height
              tempMessage.content.info = {
                ...tempMessage.content.info,
                ...imgSizes
              }
              this.addNewMessages(tempMessage)
              this.goBottomDelayed()
            }
          }
        } else this.addNewMessages(tempMessage)

        this.goBottomDelayed()

        if (gifPath) {
          const fetchGif = await fetch(gifPath)
          const blobGif = await fetchGif.blob()
          currentFile = new File([blobGif], 'gif', { type: 'image/gif', lastModified: Date.now() })
        }

        const uploadResponse = await window.matrixSDK.uploadContent(currentFile, {
          rawResponse: false,
          type: currentFile.type,
          onlyContentUri: false,
          progressHandler: params => {
            this.replaceMessageProperty({
              id: tempId,
              value: params,
              property: 'progress'
            })
          }
        })

        let content = {
          body: currentFile.name,
          info: {
            name: currentFile.name,
            mimetype: currentFile.type,
            size: currentFile.size
          },
          msgtype: msgtype,
          url: uploadResponse.content_uri
        }

        if (msgtype === msgTypes.image) {
          content.info = {
            ...content.info,
            ...imgSizes
          }
        }

        if (this.repliedMessage) {
          content = {
            ...content,
            'm.relates_to': {
              'm.in_reply_to': {
                event_id: this.repliedMessage.event_id
              }
            }
          }
        }

        this.removeMessage({ id: tempId })
        await window.matrixSDK.sendMessage(roomId, content)
      } finally {
        this.clearReply()
        this.goBottom()
      }
    },
    scrollDown () {
      this.isShowScrollDownButton = false
      this.goBottom()
    },
    async getJoinedRoomMembers () {
      const { joined } = await window.matrixSDK.getJoinedRoomMembers(this.activeChatId)
      const members = Object.entries(joined).map(([key, value]) => {
        return {
          ...value,
          id: key,
          avatar_url: createLinkImage(value.avatar_url, 40, 40)
        }
      })
      this.setRoomMembers(members)
    },
    goBottom () {
      if (!this.$refs.messageList) return
      this.$refs.messageList.scrollTop = this.$refs.messageList.scrollHeight
    },
    goBottomDelayed () {
      this.$nextTick(() => {
        setTimeout(this.goBottom, 400)
      })
    },
    async onChatChange () {
      this.clearPickedMessages()
      this.nextPaginationToken = null
      this.resetMessages()
      this.clearReply()
      this.setCurrentRoom({ id: this.activeChatId })
      if (!this.isInviteRoom) {
        await this.fetchMessages()
        if (this.currentRoom.selfMembership !== selfMembershipTypes.leave) {
          this.getJoinedRoomMembers()
        }
        this.goBottom()
        this.goBottomDelayed()
        this.infiniteId++
      }
    },
    async fetchMessages () {
      const syncToken = window.matrixSDK.store.syncToken
      const paginationToken = this.nextPaginationToken ? this.nextPaginationToken : syncToken
      const response = await this.getMessages({ roomId: this.activeChatId, from: paginationToken })

      const checkIsEditMessage = e => e.content['m.new_content']

      const clonedMessages = cloneDeep(response.chunk)
      await this.setReadMarkers(clonedMessages.reverse())

      const filteredMessages = response.chunk
        .filter(e => !checkIsEditMessage(e))
        .filter(e => {
          return !(e.type === eventTypes.member && e.content.avatar_url)
        })

      this.addMessages(filteredMessages.reverse())
      this.nextPaginationToken = response.end

      return response.chunk
    },
    async setReadMarkers (messages) {
      const { userId } = window.matrixSDK.credentials
      const fidLastMessage = findLast(messages, e => e.type === eventTypes.message && e.sender !== userId)
      if (fidLastMessage) {
        await window.matrixSDK.setRoomReadMarkersHttpRequest(
          this.activeChatId,
          fidLastMessage.event_id,
          fidLastMessage.event_id,
          fidLastMessage.event_id
        )
        const room = window.matrixSDK.getRoom(this.activeChatId)
        const formattedRoom = formattedRoomItem(room)
        this.replaceRoom(formattedRoom)
      }
    },
    async infiniteHandler ($state) {
      if (this.isLoading) return
      const result = await this.fetchMessages()
      if (result.length) $state.loaded()
      else $state.complete()
    },
    isHideUser (message, index, isPositive = false) {
      const delta = 20 * 60 * 1000 // 20 min
      const nextMessage = this.messages[isPositive ? index + 1 : index - 1]
      if (!nextMessage) {
        return false
      }
      const isSameUser = message.sender === nextMessage.sender
      const isLessThanDelta =
        new Date(message.origin_server_ts) * 1 - new Date(nextMessage.origin_server_ts) * 1 < delta
      return isSameUser && isLessThanDelta
    },
    isPrevMessageEvent (message, index) {
      const prevMessage = this.messages[index - 1]
      if (!prevMessage) {
        return false
      }
      return prevMessage.type !== eventTypes.message
    },
    isShowDay (message) {
      const dayBeginTimestamp = startOfDay(new Date(message.origin_server_ts)) * 1
      const firstMessage = this.firstMessagesByDay[dayBeginTimestamp]
      if (!firstMessage) return false
      return firstMessage.origin_server_ts === message.origin_server_ts
    },
    async onSubmit () {
      if (this.editedMessage) {
        await this.editText()
        return
      }

      if (this.attachment) {
        const payload = {
          file: this.attachment,
          roomId: this.activeChatId
        }
        this.clearAttachment()
        await this.sendAttachment(payload)
      }
      await this.sendText()
    },
    prepareMessagesData (params) {
      const { text } = this
      this.text = ''
      let data = {
        chatId: this.activeChatId,
        msgtype: msgTypes.text,
        type: eventTypes.message,
        ...params
      }
      if (!text) {
        return [data]
      }
      const textChunks = chunkText(text, 10000)
      return textChunks.map(textChunk => {
        const chunkData = cloneDeep(data)
        if (urlRegex().test(textChunk)) {
          chunkData.properties = chunkData.properties || {}
          chunkData.properties.hasLink = true
        }
        return {
          body: textChunk,
          ...chunkData
        }
      })
    },
    async sendText () {
      if (!this.text) return
      const messagesDataList = this.prepareMessagesData()
      setTimeout(this.goBottom, 0)
      for (const data of messagesDataList) {
        const { chatId, type, body, msgtype } = data
        let content = {
          body: body,
          msgtype: msgtype
        }
        if (this.repliedMessage) {
          content = {
            ...content,
            'm.relates_to': {
              'm.in_reply_to': {
                event_id: this.repliedMessage.event_id
              }
            }
          }
        }

        await window.matrixSDK.sendEvent(chatId, type, content, '').then(async () => {
          await window.matrixSDK.sendTyping(this.activeChatId, false)
        })
      }
      this.clearReply()
    },
    async editText () {
      if (!this.text) return
      const message = this.editedMessage
      const chatId = this.activeChatId
      const type = message.type
      const eventId = message.event_id
      const content = {
        body: ` * ${this.text}`,
        msgtype: msgTypes.text,
        'm.new_content': {
          body: `${this.text}`,
          msgtype: msgTypes.text
        },
        'm.relates_to': {
          event_id: eventId,
          rel_type: msgTypes.replace
        }
      }
      window.matrixSDK.sendEvent(chatId, eventId, type, content)
      this.text = ''
      this.clearEdit()
    },
    clearAttachment () {
      this.attachment = null
      this.$refs.fileInput1.clear()
      this.$refs.fileInput2.clear()
    },
    async onFileChange (file) {
      this.attachment = file
    },
    attachmentSizeValidation (file) {
      if (!file) return false
      const { size, type } = file
      const fileType = type.split('/')[0]
      const sizeMb = size * Math.pow(10, -6)
      if (this.isWAChat) {
        if (FILE_LIMIT_WA[fileType] && sizeMb > FILE_LIMIT_WA[fileType]) {
          this.$modal.show('info', {
            text: this.$t('chat.fileSizeMbLimitInfo', { n: FILE_LIMIT_WA[fileType] })
          })
          return false
        } else {
          if (sizeMb > FILE_LIMIT_WA.other) {
            this.$modal.show('info', {
              text: this.$t('chat.fileSizeMbLimitInfo', { n: FILE_LIMIT_WA.other })
            })
            return false
          }
        }
      } else {
        if (sizeMb > FILE_SIZE_LIMIT) {
          this.$modal.show('info', {
            text: this.$t('chat.fileSizeMbLimitInfo', { n: FILE_SIZE_LIMIT })
          })
          return false
        }
      }
      return true
    },
    async sendAttachment ({ file, roomId, duration = 0, gifPath = null }) {
      if (!gifPath) {
        if (!this.attachmentSizeValidation(file)) {
          return
        }
      }
      await this.sendAttachmentMessage({
        file: file,
        roomId: roomId,
        duration: duration,
        gifPath: gifPath
      })
    },
    insertToText (symbol) {
      this.text += symbol
    },
    async sendGif (gif) {
      await this.sendAttachment({
        file: null,
        roomId: this.activeChatId,
        gifPath: gif.media_formats.gif.url
      })
    },
    pickMessage ($event) {
      if ($event.type === eventTypes.message && !$event.redacted_because) {
        if (this.pickedMessages.length && this.pickedMessages.some(e => e.event_id === $event.event_id)) {
          this.clearPickedMessages()
        } else {
          this.clearPickedMessages()
          this.pickedMessages.push($event)
        }
      } else {
        this.clearPickedMessages()
      }
    },
    clearPickedMessages () {
      this.pickedMessages = []
    },
    isHideIconOnHover (msg) {
      return msg.type !== eventTypes.message || !!msg.redacted_because
    },
    isMessagePicked (message) {
      return this.pickedMessages.some(e => e.event_id === message.event_id)
    },
    async onTranslate () {
      if (!this.isPremium) return
      const targetLang = this.$store.state.entities.translatedMessages.language
      if (!targetLang) {
        this.$modal.show('settings')
        return
      }
      this.isTranslating = true
      for (const item of this.pickedMessages) {
        const requestPayload = {
          target_lang: targetLang,
          messages: [
            {
              source_text: item.content.formatted_body || item.content.body,
              message_id: item.event_id
            }
          ]
        }
        try {
          const { data } = await api.translate.translate(requestPayload)
          this.translateMessage({
            new_body: data[0].targetText,
            event_id: item.event_id,
            isTranslated: true,
            format: item.content.formatted_body ? 'html' : 'text'
          })
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error(error)
        }
      }

      this.isTranslating = false
      this.clearPickedMessages()
    },
    onForward () {
      this.$modal.show('forward-to', {
        messageIdList: this.pickedMessages,
        provider: messengerProviders.matrix,
        onClose: () => {
          this.clearPickedMessages()
        }
      })
    },
    onReply () {
      if (!this.isShowSingleActions) return
      this.repliedMessage = this.pickedMessages[0]
      this.clearPickedMessages()
    },
    onEdit () {
      this.editedMessage = this.pickedMessages[0]
      this.text = this.editedMessage.content.body
      this.clearPickedMessages()
    },
    onTextKeyupUp () {
      if (this.text) return
      const lastUserMessage = findLast(this.messages, msg => {
        return msg.senderId === this.user.id && msg.type === TYPE_CODE.text
      })
      if (!lastUserMessage) return
      this.editedMessage = lastUserMessage
      this.text = this.editedMessage.payload
    },
    onDelete () {
      this.$modal.show('delete-matrix-messages')
    },
    clearReply () {
      this.repliedMessage = null
    },
    clearEdit () {
      this.editedMessage = null
      this.text = ''
    },
    onDragEnter () {
      this.isDropFile = true
    },
    onDragLeave () {
      this.isDropFile = false
    },
    onDrop (event) {
      const file = get(event, 'dataTransfer.files[0]')
      this.isDropFile = false
      if (!file) return
      this.onFileChange(file)
    },
    onTextInput () {
      this.onTypingThrottle()
      this.onTypingDebounce()
    },
    onTypingDebounce: debounce(function () {
      this.isTyping = false
      window.matrixSDK.sendTyping(this.activeChatId, this.isTyping)
      this.isTyping = true
    }, 4000),
    onTypingThrottle: throttle(function () {
      window.matrixSDK.sendTyping(this.activeChatId, true)
    }, 2000),
    onVoice () {
      this.isVoiceRecording = true
    },
    async sendVoice ({ file, duration }) {
      this.isVoiceRecording = false
      await this.sendAttachment({
        file: file,
        roomId: this.activeChatId,
        duration: duration
      })

      setTimeout(this.goBottom, 0)
    },
    handleScroll () {
      this.isShowScrollDownButton =
        this.$refs.messageList.offsetHeight + this.$refs.messageList.scrollTop + 600 <
        this.$refs.messageList.scrollHeight
    }
  }
}
</script>
<style lang="scss">
.scroll-down {
  z-index: 3;
  position: absolute;
  right: 20px;
  bottom: 20px;
  width: 35px;
  height: 35px;
  border-radius: 50%;
  padding: 0;
  margin: 0;
  cursor: pointer;
  background: none;
  border: none;

  svg {
    width: 100%;
    height: 100%;
  }

  &:hover {
    svg {
      animation: vertical-shaking 0.7s infinite;
    }
  }

  @keyframes vertical-shaking {
    0% {
      transform: translateY(0);
    }
    25% {
      transform: translateY(1.5px);
    }
    50% {
      transform: translateY(-1.5px);
    }
    75% {
      transform: translateY(1.5px);
    }
    100% {
      transform: translateY(0);
    }
  }
}
</style>
<style module lang="scss">
%padding {
  padding: 0 50px;
}

.inviteTitle {
  font-size: 20px;
}

.inviteDescription {
  font-size: 16px;
  margin-top: 10px;
  line-height: 1.4;

  a {
    &:hover {
      text-decoration: underline;
    }
  }
}

.inviteMessageIcon {
  display: flex;
  justify-content: center;
  margin-bottom: 10px;

  svg {
    path {
      transform: scale(1.7);
    }

    width: 80px;
    height: 80px;
    fill: $text-error;
    margin-bottom: 10px;
  }
}

.inviteAvatar {
  align-items: center;
  display: flex;
  justify-content: center;
  margin: 20px 0;
}

.inviteContainer {
  width: 100%;
  max-width: 80%;
  margin: 0 auto;
  text-align: center;
  padding: 24px 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1 0;
  overflow-y: auto;
}

.inviteBtns {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 20px;

  button {
    margin: 0 20px;
  }
}

.container {
  // flex-basis: 0;
  flex-grow: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: relative;
}

.dropzone {
  position: absolute;
  height: 100%;
  width: 100%;
  z-index: 4;
  text-align: center;
  background-image: linear-gradient(90deg, $text-mid 50%, #fff 50%), linear-gradient(90deg, $text-mid 50%, #fff 50%),
    linear-gradient(0deg, $text-mid 50%, #fff 50%), linear-gradient(0deg, $text-mid 50%, #fff 50%);
  background-repeat: repeat-x, repeat-x, repeat-y, repeat-y;
  background-size: 15px 2px, 15px 2px, 2px 15px, 2px 15px;
  background-position: left top, right bottom, left bottom, right top;
  animation: border-dance 0.5s infinite linear;

  * {
    pointer-events: none;
  }

  &Text {
    top: 50%;
    position: relative;
    z-index: 3;
    font-size: 16px;
    color: $text-mid-dark;
  }

  &Icon {
    font-size: 30px;
    margin-bottom: 16px;
  }

  &::before {
    content: '';
    position: absolute;
    z-index: 3;
    top: 2px;
    left: 2px;
    height: calc(100% - 4px);
    width: calc(100% - 4px);
    background-color: rgba(#fff, 0.9);
  }
}

@keyframes border-dance {
  0% {
    background-position: left top, right bottom, left bottom, right top;
  }
  100% {
    background-position: left 15px top, right 15px bottom, left bottom 15px, right top 15px;
  }
}

.placeholder {
  flex-grow: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  text-align: center;
  font-size: 16px;
  color: $text-mid;
}

.infiniteLoading {
  height: 50px;
  padding-top: 5px;
}

.messageListWrap {
  flex-grow: 1;
  overflow-x: hidden;
  overflow-y: auto;
  position: relative;
}

.messageList {
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  min-height: 100%;
  padding-bottom: 20px;
  width: 100%;

  &Active > .messageItem:hover > :global(.gl-message-container) {
    background-color: rgba($main-bg-colored, 0.2);
  }
}

.bottomWrap {
  min-height: 70px;
  border: 1px solid transparent;
  border-top: 1px solid $border-color;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  position: relative;
}

.extraBlock {
  position: relative;
  width: 410px;
  margin-left: 104px;
  padding-right: 12px;
}

.attachmentPreview {
  color: $text-colored;
  position: relative;
  margin: 4px 0;
  display: flex;
  padding-right: 44px;

  &Body {
    flex-grow: 1;
    min-width: 0;
  }

  &Name {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    padding: 0 8px;
  }

  &Size {
    flex-shrink: 0;
    color: $text-mid;
  }

  &Close {
    position: absolute;
    right: 0;
    top: 3px;
    font-size: 10px;
    cursor: pointer;
    color: $text-dark;
  }
}

.sendMessageForm {
  @extend %padding;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-bottom: 5px;
}

.sendMessageFormActive {
  @extend %padding;
  display: flex;
  justify-content: space-between;
}

.avatarContainer {
  padding-right: 14px;
  flex-shrink: 0;
}

.messageContainer {
  // flex-grow: 1;
  flex-basis: 410px;
  margin-right: 14px;
  position: relative;
}

.inputSubject,
.input {
  border: none;
  position: relative;
  z-index: 2;
  border-bottom: 1px solid $transparent;
}

.inputSubject {
  font-size: 12px;
  margin-bottom: 4px;
  width: 40%;

  &::placeholder {
    color: $text-light;
    font-size: 10px;
  }

  &:focus::placeholder {
    color: $text-mid;
  }
}

.input {
  width: 100%;

  &:focus {
    border-bottom: 1px solid $main-bg-colored;
  }
}

.buttons {
  flex-shrink: 0;
  padding-left: 12px;
}

.buttonsActive {
  flex-shrink: 0;
  padding-left: 12px;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.reply {
  width: 100%;
  position: absolute;
  z-index: 1;
  top: -40px;
  // transition: transform 0.1s;
}

.buttonSend {
  text-transform: uppercase;
  background-color: #fff;
  color: $text-colored;
  font-weight: 600;
  cursor: pointer;
  display: flex;
  align-items: center;

  &Icon {
    transform: rotate(135deg);
    margin-left: 6px;
    font-size: 18px;
  }
}

.buttonsMore {
  display: flex;
  flex-basis: 50%;
  align-items: center;
  margin-bottom: 12px;
}

.btnMore {
  color: $text-light;
  margin-right: 10px;
  font-size: 20px;
  cursor: pointer;
  transition: all 0.1s;

  &:hover {
    transform: scale(1.1);
  }
}

.actions {
  flex-grow: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;

  @extend %padding;

  &Group {
    display: flex;
  }

  &Button:global(.form--button.button--oval) {
    margin-right: 10px;
    width: auto;
  }
}

.replyClose {
  color: $text-light;
  cursor: pointer;
  font-size: 14px;
  margin-top: 4px;
  z-index: 3;
  position: absolute;
  right: 0;
  top: 4px;
  background: #fff;
}

:global(.tooltip.popover) .picker:global(.popover-inner) {
  border-radius: 4px;
}

.mentionUserList {
  padding: 10px 0;
  max-height: 240px;
  overflow-y: scroll;
}

.mentionUserItem {
  padding: 0 8px;
  line-height: 24px;
  cursor: pointer;

  &:hover {
    color: $text-lightest;
    background-color: $text-success;
  }
}
</style>
